// bm-design-system: form field + error summary primitives
import {
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
} from "react"

// Error message ids are derived from the control's id so an input can point at
// its own message via aria-describedby. Single source of truth — both Field and
// any hand-wired control (radio fieldsets, say) must use this.
export const errorId = (id: string) => `${id}-error`
export const hintId = (id: string) => `${id}-hint`

/**
 * Labelled form control that wires up its own error state.
 *
 * The control is cloned rather than configured by the caller, so no form has to
 * remember to repeat the aria attributes — that repetition is exactly how the
 * audit forms ended up shipping without them. An invalid field gets
 * aria-invalid plus aria-describedby pointing at its message, so a screen
 * reader announces the reason when focus lands on the control.
 */
export function Field({
  label,
  htmlFor,
  error,
  optional,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  error?: string
  optional?: boolean
  /** Short guidance under the label. Announced with the control, not instead
      of the error — a field can carry both, and describedby lists both ids. */
  hint?: string
  children: ReactNode
}) {
  const describedBy = [hint && hintId(htmlFor), error && errorId(htmlFor)]
    .filter(Boolean)
    .join(" ")

  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor}>
        {label}
        {optional && <span className="ml-1 font-normal text-ink-muted">(optional)</span>}
      </label>
      {hint && (
        <p id={hintId(htmlFor)} className="text-xs text-ink-muted">
          {hint}
        </p>
      )}
      {isValidElement(children) && describedBy
        ? cloneElement(children as ReactElement<Record<string, unknown>>, {
            ...(error ? { "aria-invalid": true } : {}),
            "aria-describedby": describedBy,
          })
        : children}
      {error && (
        <p id={errorId(htmlFor)} className="text-xs text-danger-display">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * Summary of everything wrong with a submission, rendered above the form.
 *
 * role="alert" announces it the moment it appears, and focus moves here on each
 * failed submit — without that, a rejected submission silently returns the user
 * to a form that looks unchanged. Each entry links to its control, so the list
 * is a way to navigate as well as to read.
 *
 * `fieldId` maps a server-side error key to the DOM id of its control. It
 * defaults to identity, which is right when they already match; forms that
 * suffix their ids (to render twice on a page) or whose param name differs from
 * the model attribute pass their own.
 */
export function ErrorSummary({
  errors,
  subject,
  fieldId = (key) => key,
}: {
  errors: Record<string, string>
  subject: string
  fieldId?: (key: string) => string
}) {
  const entries = Object.entries(errors)
  const count = entries.length
  const ref = useRef<HTMLDivElement>(null)

  // Re-focus on every failed submit, not just the first: `errors` is a fresh
  // object each time, so a second failure re-announces rather than going quiet.
  useEffect(() => {
    if (count > 0) ref.current?.focus()
  }, [count, errors])

  if (count === 0) return null

  return (
    <div
      ref={ref}
      role="alert"
      tabIndex={-1}
      className="mt-8 rounded-xl border border-danger-display bg-surface p-5 focus:outline-none"
    >
      <h2 className="text-danger-display">
        {count === 1
          ? `There is 1 problem with your ${subject}`
          : `There are ${count} problems with your ${subject}`}
      </h2>
      <ul className="mt-3 space-y-1">
        {entries.map(([field, message]) => (
          <li key={field}>
            <a href={`#${fieldId(field)}`} className="text-sm text-danger-display">
              {message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
